import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Globe } from "lucide-react";
import LiveTruckIndicator from "@/components/LiveTruckIndicator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import NotificationHub from "@/components/NotificationHub";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import { CompactLocaleSelector } from "@/components/CompactLocaleSelector";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useNavOverflow } from "@/hooks/useNavOverflow";
import NavMoreMenu from "@/components/NavMoreMenu";
import { useActiveTrips } from "@/hooks/useActiveTrips";

const heroPages = ["/", "/how-it-works", "/for-couriers", "/about", "/faq", "/support"];

const NAV_ITEMS = [
  { key: 'how_it_works', href: '/how-it-works', labelKey: 'nav.howItWorks' },
  { key: 'for_couriers', href: '/for-couriers', labelKey: 'nav.forCouriers' },
  { key: 'faq', href: '/faq', labelKey: 'nav.faq' },
  { key: 'support', href: '/support', labelKey: 'nav.support' },
  { key: 'about', href: '/about', labelKey: 'nav.about' }
];

const getPortalDestination = (role?: string) => {
  switch (role) {
    case "courier":
      return "/courier";
    case "admin":
      return "/admin";
    case "support":
      return "/support-portal";
    case "affiliate":
      return "/affiliate";
    default:
      return "/account";
  }
};

const getPortalLabelKey = (role?: string) => {
  switch (role) {
    case "courier":
      return "nav.courierPortal";
    case "admin":
      return "nav.adminPanel";
    case "support":
      return "nav.supportPortal";
    case "affiliate":
      return "nav.myAccount";
    default:
      return "nav.myAccount";
  }
};

const Navbar = () => {
  const { t } = useTranslation();
  const { localePath } = useLocalizedPath();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileLocaleOpen, setMobileLocaleOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { activeTripId } = useActiveTrips();
  const strippedPath = location.pathname.replace(/^\/(sv|fr|es|it|ar)/, '') || '/';
  const isHeroPage = heroPages.includes(strippedPath);

  const navContainerRef = useRef<HTMLElement>(null);
  const mobileLocaleRef = useRef<HTMLDivElement>(null);

  const navItems = NAV_ITEMS.map(item => ({
    ...item,
    label: t(item.labelKey)
  }));

  const { visibleItems, overflowItems } = useNavOverflow(
    navItems,
    navContainerRef,
    t('nav.menu'),
    true
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile locale dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (mobileLocaleRef.current && !mobileLocaleRef.current.contains(e.target as Node)) {
        setMobileLocaleOpen(false);
      }
    };
    if (mobileLocaleOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [mobileLocaleOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileLocaleOpen(false);
  }, [location.pathname]);

  const navDark = isHeroPage && !scrolled;

  return (
    <header
      role="banner"
      className={cn(
        "fixed top-0 left-0 right-0 z-[var(--z-nav)] w-full transition-[background-color,box-shadow] duration-[var(--duration-mid)] ease-[var(--ease)]",
        scrolled || !isHeroPage
          ? "glass-nav shadow-sm"
          : "nav-transparent"
      )}
      style={{ zIndex: 'var(--z-nav, 30)' }}
    >
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-background focus:text-foreground focus:px-4 focus:py-2 focus:rounded focus:shadow-lg"
      >
        {t('skipToContent', 'Skip to main content')}
      </a>
      <div className="container flex h-[72px] items-center justify-between">
        {/* Logo */}
        <Link to={localePath("/")} className="flex items-center shrink-0" aria-label={t('nav.homeAria', 'EuroCollis home')}>
          <img src="/logo-small.png" alt="EuroCollis" className="h-10 w-auto" width="40" height="40" {...({ fetchpriority: "high" } as any)} />
        </Link>

        {/* Desktop nav links — NO overflow-hidden so dropdowns aren't clipped */}
        <div className="hidden lg:flex flex-1 justify-center px-4">
          <nav
            ref={navContainerRef}
            className="flex items-center gap-1"
            aria-label={t('nav.mainNavAria', 'Main navigation')}
          >
            {visibleItems.map((item) => (
              <Link
                key={item.key}
                to={localePath(item.href)}
                className={cn(
                  "px-3 py-2 text-sm font-semibold transition-colors duration-[var(--duration-fast)] ease-[var(--ease)] whitespace-nowrap",
                  location.pathname === localePath(item.href)
                    ? "text-[var(--color-orange)]"
                    : "text-white/70 hover:text-white"
                )}
              >
                {item.label}
              </Link>
            ))}
            {overflowItems.length > 0 && (
              <NavMoreMenu items={overflowItems} navDark={navDark} />
            )}
          </nav>
        </div>

        {/* Desktop right actions */}
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          {/* Live trip indicator for couriers */}
          {isAuthenticated && user?.role === "courier" && activeTripId && (
            <Link
              to={`/courier/trips/${activeTripId}/execute`}
              aria-label={t("portal.layout.live")}
              className="inline-flex items-center self-center leading-none"
            >
              <LiveTruckIndicator size={28} paused={!activeTripId} ariaHidden />
            </Link>
          )}
          <CompactLocaleSelector />
          {isAuthenticated && <NotificationHub />}
          {isAuthenticated ? (
            <>
              <WorkspaceSwitcher variant="desktop" />
              <Button
                variant="ghost"
                size="sm"
                className="font-semibold h-10 px-4 text-white/70 hover:text-white hover:bg-white/10 text-sm"
                onClick={logout}
              >
                {t('nav.signOut')}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="font-semibold h-10 px-4 border-white/20 bg-white/[0.06] text-white hover:bg-white/10 text-sm"
              asChild
            >
              <Link to={localePath("/login")}>{t('nav.signIn')}</Link>
            </Button>
          )}
          <Button
            size="sm"
            className="border-2 border-[var(--color-orange)] bg-transparent text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy)] h-10 px-4 text-sm shadow-sm"
            asChild
          >
            <Link to={localePath("/quote")}>{t('nav.getQuote')}</Link>
          </Button>
        </div>

        {/* Mobile: Live + Globe + Hamburger */}
        <div className="lg:hidden flex items-center gap-1">
          {/* Live trip indicator for couriers — mobile */}
          {isAuthenticated && user?.role === "courier" && activeTripId && (
            <Link
              to={`/courier/trips/${activeTripId}/execute`}
              aria-label={t("portal.layout.live")}
              className="inline-flex items-center self-center leading-none"
            >
              <LiveTruckIndicator size={28} paused={!activeTripId} ariaHidden />
            </Link>
          )}
          {isAuthenticated && <NotificationHub />}
          <div className="relative" ref={mobileLocaleRef}>
            <button
              className="p-2.5 text-white"
              onClick={() => { setMobileLocaleOpen(!mobileLocaleOpen); setMobileOpen(false); }}
              aria-label={t('localeSelector.languageAndCurrency', 'Language & Currency')}
            >
              <Globe className="h-5 w-5" aria-hidden="true" />
            </button>
            {mobileLocaleOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-64 bg-[var(--color-navy)] border border-white/10 shadow-2xl z-50 overflow-hidden max-h-[70dvh] overflow-y-auto">
                <CompactLocaleSelector variant="mobile-dropdown" onSelect={() => setMobileLocaleOpen(false)} />
              </div>
            )}
          </div>
          <button
            className="p-2.5 text-white"
            onClick={() => { setMobileOpen(!mobileOpen); setMobileLocaleOpen(false); }}
            aria-label={mobileOpen ? t('nav.closeMenu', 'Close menu') : t('nav.openMenu', 'Open menu')}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — slide-down with staggered items */}
      <div
        className={cn(
          "lg:hidden border-t border-white/10 glass-nav overflow-hidden transition-all duration-[var(--duration-mid)] ease-[var(--ease)]",
          mobileOpen ? "max-h-[calc(100dvh-72px)] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        )}
      >
        <nav className="container flex flex-col py-5 gap-1" aria-label={t('nav.mobileNavAria', 'Mobile navigation')}>
          {NAV_ITEMS.map((item, i) => (
            <Link
              key={item.key}
              to={localePath(item.href)}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "px-4 py-3.5 text-base font-semibold transition-all duration-[var(--duration-fast)] ease-[var(--ease)] active:scale-[0.98]",
                mobileOpen ? "translate-y-0 opacity-100" : "-translate-y-2 opacity-0",
                location.pathname === localePath(item.href)
                  ? "text-[var(--color-orange)] bg-white/[0.04]"
                  : "text-white/70 hover:text-white hover:bg-white/[0.04]"
              )}
              style={{ transitionDelay: mobileOpen ? `${i * 40}ms` : '0ms' }}
            >
              {t(item.labelKey)}
            </Link>
          ))}




          <div className="pt-3 px-1 flex flex-col gap-3"
            style={{ transitionDelay: mobileOpen ? `${NAV_ITEMS.length * 40 + 60}ms` : '0ms' }}
          >
            {isAuthenticated ? (
              <>
                <WorkspaceSwitcher variant="mobile" onNavigate={() => setMobileOpen(false)} />
                <Button
                  variant="ghost"
                  className="w-full font-semibold h-12 text-base text-white/70 hover:text-white hover:bg-white/10 active:scale-[0.98] transition-transform"
                  onClick={() => { logout(); setMobileOpen(false); }}
                >
                  {t('nav.signOut')}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                className="w-full font-semibold h-12 text-base border-white/20 bg-white/[0.06] text-white hover:bg-white/10 active:scale-[0.98] transition-transform"
                asChild
              >
                <Link to={localePath("/login")} onClick={() => setMobileOpen(false)}>
                  {t('nav.signIn')}
                </Link>
              </Button>
            )}
            <Button
              className="w-full border-2 border-[var(--color-orange)] bg-transparent text-[var(--color-orange)] font-semibold hover:bg-[var(--color-navy)] h-12 shadow-sm text-base active:scale-[0.98] transition-transform px-8"
              asChild
            >
              <Link to={localePath("/quote")} onClick={() => setMobileOpen(false)}>
                {t('nav.getQuote')}
              </Link>
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
